import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, Play, Volume2, VolumeX, ArrowRight,
  ScrollText, Image as ImageIcon, BarChart3, FileCheck2, Sparkles, Lock, Layers
} from "lucide-react";

// Videos are shipped inside the frontend build (frontend/public/media/) so they
// reach production via the frontend image (the platform deploy currently does
// not ship backend/public/* binaries). Falls back gracefully if anyone re-routes
// to the backend mount.
const MEDIA = "/media";

const HIT_POINTS = [
  {
    icon: Layers,
    title: "Five-portal auto-classification",
    body: "EW Finalisation, IRW Scope, IRW Finalisation, EPAR Scope and EPAR Finalisation — packages routed at over 95% confidence with a live ingest progress bar.",
  },
  {
    icon: ScrollText,
    title: "15,000-row ledger in 1.8 seconds",
    body: "Streaming openpyxl iter_rows reconciliation against the NSWRA on-cost fact sheet. Line by line. Every line overridable by the assessor.",
  },
  {
    icon: ImageIcon,
    title: "Photo evidence per Damage Memo",
    body: "EXIF, GPS and quality checks. Photos bucketed per DM. Strong / Adequate / Insufficient call surfaced against the NSWRA evidence rules.",
  },
  {
    icon: BarChart3,
    title: "CET vs Standard Treatment Guide",
    body: "Workbooks checked against STG v2.1. Suspect draft stubs flagged. Aggregate CET parsing identifies LGA + project for parent folders and on-cost anomalies.",
  },
  {
    icon: Sparkles,
    title: "Precedent corpus — cited, not hidden",
    body: "Every Accept · RFI · Reject is recorded with the assessor's free-text rationale. Indexed by job, by assessor, by date. Cited explicitly inside the DRFA Expert's next answer.",
  },
  {
    icon: FileCheck2,
    title: "Exact SmartyGrants artefacts at sign-off",
    body: "EW Assessment Spreadsheet and EPAR Scope SQP Assessment Record generated bit-for-bit to template — ready for SharePoint and push-back upstream.",
  },
];

const PHASES = [
  {
    tag: "Phase 1 · Today",
    head: "The DRFA Agent assists. The SQP decides every line.",
    body: "The platform classifies, parses, reconciles, surfaces options. The assessor clicks Accept, RFI or Reject. The click is what is written to the artefact.",
  },
  {
    tag: "Phase 2 · Next",
    head: "Defaults on the easy ones. Assessor confirms in one click.",
    body: "On high-confidence, low-risk lines the Agent pre-selects the recommended outcome. The SQP one-click confirms or overrides. Programme Director sign-off and NSWRA notification before each portal turns on.",
  },
  {
    tag: "Phase 3 · Endgame",
    head: "The DRFA Agent IS the SQP, where it has out-performed the human baseline.",
    body: "Category by category, against a published audit benchmark, the Agent assumes the assessor mark. Humans handle escalations, governance and audit.",
  },
];

function VideoBlock({ src, poster, autoPlay = false, label, testid }) {
  const ref = useRef(null);
  const [muted, setMuted] = useState(autoPlay);
  const [playing, setPlaying] = useState(autoPlay);

  const togglePlay = () => {
    const v = ref.current; if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); } else { v.pause(); setPlaying(false); }
  };
  const toggleMute = () => {
    const v = ref.current; if (!v) return;
    v.muted = !v.muted; setMuted(v.muted);
    if (v.paused && !v.muted) { v.play(); setPlaying(true); }
  };

  return (
    <div className="relative rounded-xl overflow-hidden bg-black ring-1 ring-white/10 shadow-2xl" data-testid={testid}>
      <video
        ref={ref}
        src={src}
        poster={poster}
        className="w-full block"
        autoPlay={autoPlay}
        muted={autoPlay}
        loop={autoPlay}
        playsInline
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        controls={!autoPlay}
      />
      {autoPlay && (
        <div className="absolute bottom-3 right-3 flex items-center gap-2">
          <button
            onClick={toggleMute}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur text-white text-[11px] tracking-[0.14em] uppercase font-semibold hover:bg-black/85 border border-white/15"
            data-testid={`${testid}-mute-toggle`}
          >
            {muted ? <><VolumeX className="w-3.5 h-3.5"/> Watch with sound</> : <><Volume2 className="w-3.5 h-3.5"/> Mute</>}
          </button>
          <button
            onClick={togglePlay}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-white/95 text-slate-900 text-[11px] tracking-[0.14em] uppercase font-semibold hover:bg-white"
            data-testid={`${testid}-play-toggle`}
          >
            <Play className="w-3.5 h-3.5"/> {playing ? "Pause" : "Play"}
          </button>
        </div>
      )}
      {label && (
        <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-black/70 text-white text-[10px] tracking-[0.18em] uppercase font-bold border border-white/15">
          {label}
        </div>
      )}
    </div>
  );
}

export function Showcase() {
  return (
    <div className="min-h-screen bg-[#0b1228] text-slate-100" data-testid="showcase-page">
      {/* Top bar */}
      <header className="border-b border-white/10 bg-[#0b1228]/85 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-white flex items-center justify-center">
              <ShieldCheck className="w-5 h-5" style={{color:"#002664"}}/>
            </div>
            <div>
              <div className="font-heading font-bold text-[15px] leading-none">RAAgent</div>
              <div className="text-[10px] tracking-[0.18em] uppercase text-slate-400 mt-0.5">RHE Project Consulting · drfa-assess.com</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/security"
              className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10.5px] tracking-[0.14em] uppercase font-bold bg-emerald-500/15 text-emerald-300 border border-emerald-400/30 hover:bg-emerald-500/25"
              data-testid="showcase-security-link"
            >
              <Lock className="w-3 h-3"/> Security &amp; AI
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white text-slate-900 text-[12px] font-semibold hover:bg-slate-100"
              data-testid="showcase-signin-link"
            >
              Sign in <ArrowRight className="w-3.5 h-3.5"/>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-5 lg:px-8 pt-12 lg:pt-20 pb-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-5">
            <div className="text-[11px] tracking-[0.22em] uppercase text-emerald-300/90 font-bold mb-4">Audit-grade DRFA assessment</div>
            <h1 className="font-heading text-[40px] sm:text-5xl lg:text-[56px] leading-[1.04] tracking-tight font-bold mb-6">
              Days of assessment work.
              <span className="block text-emerald-300">Down to one afternoon.</span>
              <span className="block text-slate-300 text-3xl lg:text-4xl mt-3 font-semibold">Without taking a single decision out of the assessor&apos;s hands.</span>
            </h1>
            <p className="text-slate-300/90 text-[15px] leading-relaxed mb-7 max-w-xl">
              RAAgent ingests SmartyGrants disaster-recovery packages, reconciles fifteen-thousand-row labour ledgers in under two seconds, buckets every photo per Damage Memo, and surfaces Accept · RFI · Reject options for the SQP — with the matching past precedent already cited.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Link to="/login" className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-emerald-400 text-slate-900 text-[13px] font-bold tracking-tight hover:bg-emerald-300" data-testid="showcase-cta-signin">
                Open the portal <ArrowRight className="w-4 h-4"/>
              </Link>
              <a href="#walkthrough-ew" className="inline-flex items-center gap-2 px-5 py-3 rounded-md border border-white/20 text-[13px] font-semibold hover:bg-white/5" data-testid="showcase-cta-walkthrough">
                Watch the EW walkthrough · 7:33
              </a>
            </div>
            <div className="mt-7 flex flex-wrap gap-4 text-[11px] tracking-[0.14em] uppercase text-slate-400 font-mono">
              <span>NSW DAG 2021</span>
              <span className="text-slate-600">·</span>
              <span>STG v2.1</span>
              <span className="text-slate-600">·</span>
              <span>EPAR Guidelines Dec 2025</span>
              <span className="text-slate-600">·</span>
              <span>SmartyGrants templates</span>
            </div>
          </div>
          <div className="lg:col-span-7">
            <VideoBlock
              src={`${MEDIA}/RAAgent_Lead_Marketing_Video.mp4`}
              autoPlay
              label="Lead video · 1:20"
              testid="showcase-hero-video"
            />
            <div className="text-[11px] text-slate-500 mt-3 px-1">Auto-playing, muted by default. Tap <span className="text-slate-300">Watch with sound</span> for narrated audio.</div>
          </div>
        </div>
      </section>

      {/* Hit points */}
      <section className="border-y border-white/5 bg-[#0a0f23]/60 py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="text-[11px] tracking-[0.22em] uppercase text-emerald-300/90 font-bold mb-3">What the platform actually does</div>
          <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight mb-12 max-w-3xl">
            Deterministic work in seconds. <span className="text-slate-400">Judgement stays with the SQP.</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 rounded-xl overflow-hidden border border-white/5">
            {HIT_POINTS.map((p, i) => (
              <div key={i} className="bg-[#0b1228] p-7 hover:bg-[#0d1735] transition" data-testid={`hit-point-${i}`}>
                <p.icon className="w-6 h-6 text-emerald-300 mb-4"/>
                <h3 className="font-heading text-[17px] font-bold mb-2 tracking-tight">{p.title}</h3>
                <p className="text-[13.5px] leading-relaxed text-slate-400">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Walkthrough — EW */}
      <section id="walkthrough-ew" className="max-w-6xl mx-auto px-5 lg:px-8 py-16 lg:py-20">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-4">
            <div className="text-[11px] tracking-[0.22em] uppercase text-emerald-300/90 font-bold mb-3">Narrated walkthrough</div>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight mb-4">
              EW Finalisation, end to end.
            </h2>
            <p className="text-slate-400 text-[14px] leading-relaxed mb-5">
              Seven minutes thirty-three. From ZIP drop into the Ingest Inbox, through ledger reconciliation, photo bucketing, defect extraction and per-line Accept · RFI · Reject — to a downloaded EW Assessment Spreadsheet.
            </p>
            <ul className="space-y-2.5 text-[13px] text-slate-300/90 mb-5">
              <li className="flex gap-2"><span className="text-emerald-300 mt-0.5">→</span> Ingest progress with live milestones</li>
              <li className="flex gap-2"><span className="text-emerald-300 mt-0.5">→</span> Ledger Checker streaming parse + override layer</li>
              <li className="flex gap-2"><span className="text-emerald-300 mt-0.5">→</span> EW defect photos and CET cost evidence</li>
              <li className="flex gap-2"><span className="text-emerald-300 mt-0.5">→</span> Single-click EW Assessment Spreadsheet</li>
            </ul>
            <div className="text-[11px] tracking-[0.14em] uppercase text-slate-500 font-mono">AU-English narrator · 13.8 MB</div>
          </div>
          <div className="lg:col-span-8">
            <VideoBlock
              src={`${MEDIA}/EW_Finalisation_Walkthrough.mp4`}
              label="EW Finalisation · 7:33"
              testid="showcase-walkthrough-ew"
            />
          </div>
        </div>
      </section>

      {/* Walkthrough — EPAR (silent placeholder) */}
      <section id="walkthrough-epar" className="border-t border-white/5 bg-[#0a0f23]/60 py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-4 order-2 lg:order-1">
            <VideoBlock
              src={`${MEDIA}/EPAR_Scope_Walkthrough_Silent.mp4`}
              label="EPAR Scope · silent preview"
              testid="showcase-walkthrough-epar"
            />
            <div className="text-[11px] tracking-[0.14em] uppercase text-amber-300/80 font-mono mt-3">Narration coming soon</div>
          </div>
          <div className="lg:col-span-8 order-1 lg:order-2">
            <div className="text-[11px] tracking-[0.22em] uppercase text-emerald-300/90 font-bold mb-3">Walkthrough preview</div>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight mb-4">EPAR Scope Approval.</h2>
            <p className="text-slate-400 text-[14px] leading-relaxed max-w-2xl">
              An EPAR Scope package can ship with sixty Damage Memos and three thousand photos, each governed by DAG 2021 + the EPAR Guidelines (Dec 2025) + the Standard Treatment Guide v2.1. RAAgent classifies, sorts, surfaces — the SQP signs. The narrated edition lands once narration credits refresh; the silent UI capture is here in the meantime.
            </p>
          </div>
        </div>
      </section>

      {/* Trajectory */}
      <section className="max-w-6xl mx-auto px-5 lg:px-8 py-16 lg:py-20">
        <div className="text-[11px] tracking-[0.22em] uppercase text-emerald-300/90 font-bold mb-3">Governed trajectory</div>
        <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight mb-3 max-w-3xl">
          The DRFA Agent assists today. It becomes the SQP tomorrow.
        </h2>
        <p className="text-slate-400 text-[14px] leading-relaxed mb-10 max-w-3xl">
          Each phase shift is a governance milestone — Programme Director sign-off, NSWRA notification, public statement update on the platform. Never a silent upgrade.
        </p>
        <div className="grid md:grid-cols-3 gap-6">
          {PHASES.map((p, i) => (
            <div key={i} className="rounded-xl p-7 bg-gradient-to-b from-white/[0.06] to-white/[0.02] border border-white/10" data-testid={`phase-${i+1}`}>
              <div className="text-[10.5px] tracking-[0.22em] uppercase font-bold text-emerald-300 mb-3">{p.tag}</div>
              <h3 className="font-heading text-[19px] font-bold tracking-tight mb-3 leading-snug">{p.head}</h3>
              <p className="text-[13.5px] leading-relaxed text-slate-400">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Closing CTA */}
      <section className="border-t border-white/5 py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center">
          <h2 className="font-heading text-3xl lg:text-5xl font-bold tracking-tight mb-5">
            Built by assessors. <span className="text-emerald-300">For assessors.</span>
          </h2>
          <p className="text-slate-300/90 text-[15px] leading-relaxed mb-8 max-w-2xl mx-auto">
            And for the NSW Reconstruction Authority that signs off the work. The AI never marks an outcome. The assessor clicks. The click is what is written.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link to="/login" className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-emerald-400 text-slate-900 text-[13.5px] font-bold hover:bg-emerald-300" data-testid="showcase-footer-signin">
              Open the portal <ArrowRight className="w-4 h-4"/>
            </Link>
            <a href="/api/presentation" target="_blank" rel="noopener" className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-white/20 text-[13.5px] font-semibold hover:bg-white/5" data-testid="showcase-footer-briefing">
              Read the full briefing deck
            </a>
            <a href="/media/RAAgent_Briefing.pdf" download className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-white/20 text-[13.5px] font-semibold hover:bg-white/5" data-testid="showcase-footer-briefing-pdf">
              Download PDF
            </a>
            <Link to="/security" className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-white/20 text-[13.5px] font-semibold hover:bg-white/5" data-testid="showcase-footer-security">
              Security &amp; AI statement
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/5 py-8">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 text-[11px] text-slate-500 tracking-[0.12em] uppercase flex flex-wrap items-center justify-between gap-3">
          <div>RHE Project Consulting · drfa-assess.com</div>
          <div>Powered by RAAgent</div>
        </div>
      </footer>
    </div>
  );
}

export default Showcase;
